/**
 * The `/settings` sub-nav's tabs: the label a reader sees for each settings
 * page, and the route it sits on.
 *
 * Its own module rather than an export from SettingsTabs.tsx, because two
 * unrelated things need it — the sub-nav renders it, and the Ask Forged
 * context pill names the page in view from it — and a component file that
 * also exports constants breaks fast refresh.
 *
 * ⚠ Not settingsTabs.ts, and the difference is not a style preference.
 * That name differs from SettingsTabs.tsx only in case, and TypeScript tries
 * the .ts extension first, so on a case-insensitive filesystem (Windows, and
 * macOS by default) the layout route's
 * `import { SettingsTabs } from "@/features/dashboard/SettingsTabs"`
 * resolved to THIS file and then failed to find the component in it. Linux CI
 * resolves it correctly and stayed green, so the typecheck was broken only for
 * the people running it locally.
 *
 * ⚠ These are the authority for what a settings page is CALLED. The v3 route
 * table answered "General" for this whole area while that was its sidebar
 * row rather than any page: `/settings` renders ProjectSettings and this list
 * calls that tab "Project". Reading the table instead put a confidently
 * wrong scope in the pill. See askForgedContextLabel.ts.
 */
/* Order is the sub-nav's left-to-right order. AI models sits before
 * Notifications (Kris, Sep 22, 2026): it was added last and landed on the
 * end, which put the setting that shapes every piece of work Forged does
 * behind the one that decides which emails arrive. Usage joined as the
 * second tab when Settings became one sidebar row with no sub rows (Kris,
 * Oct 10, 2026: "project then usage then ai models then notifications"). */
export const SETTINGS_TABS = [
  { to: "/settings", label: "Project" },
  { to: "/settings/usage", label: "Usage" },
  { to: "/settings/ai-models", label: "AI models" },
  { to: "/settings/notifications", label: "Notifications" },
] as const;

/** The screen's name: the Settings layout's h1 and the area's head title. */
export const SETTINGS_SECTION = "Settings";

type SettingsTabPath = (typeof SETTINGS_TABS)[number]["to"];

/**
 * A tab's browser tab title, the tab then the screen ("Usage, Settings"), as
 * Memory's tabs title theirs (#3685). The tabs draw no heading of their own,
 * so this is what names the tab outside the page.
 */
export function settingsTabTitle(to: SettingsTabPath): string {
  const tab = SETTINGS_TABS.find((t) => t.to === to)!;
  return `${tab.label}, ${SETTINGS_SECTION}`;
}
